import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import WarningTriangle from '../../../components/WarningTriangle';

import { DataGrid, GridActionsCellItem, GRID_STRING_COL_DEF } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';

import Popper from '@mui/material/Popper';
import { Box, Button, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import EditIcon from '@mui/icons-material/Edit';

import FixOrContinueModal from '../Modals/FixOrContinueModal';

import { GBENSContext } from '../GBENSContainer';
import { useStepper } from '../../../components/Stepper';

import RightLink from '../Components/RightLink';

function StatusCell({ value }) {
  const errorsValidation = value[0];
  const errorsHMRC = value[1];

  const warningRefHMRC = useRef();
  const [popperHMRCOpen, setPopperHMRCOpen] = useState(false);

  const handleMouseEnterHMRC = () => {
    setPopperHMRCOpen(true);
  };

  const handleMouseLeaveHMRC = () => {
    setPopperHMRCOpen(false);
  };

  if (!errorsValidation?.errors && !errorsHMRC?.errors) return <CheckIcon sx={{ color: 'green' }} />;
  return (
    <>
      {errorsValidation?.errors && (
        <WarningTriangle
          message={errorsValidation?.message}
          errors={Object.keys(errorsValidation?.errors ?? {})
            .filter((k) => k !== 'lines' && k !== 'id')
            .map((k) => errorsValidation?.errors[k])}
        />
      )}
      {errorsHMRC?.errors && (
        <>
          <IconButton ref={warningRefHMRC} onMouseEnter={handleMouseEnterHMRC} onMouseLeave={handleMouseLeaveHMRC}>
            <WarningIcon sx={{ color: 'red' }} />
          </IconButton>
          <Popper placement="right" open={popperHMRCOpen} anchorEl={warningRefHMRC.current}>
            <Box sx={{ border: 1, p: 1, bgcolor: 'black', color: 'white' }}>
              <Box>{errorsHMRC?.message}</Box>
              {Object.keys(errorsHMRC?.errors ?? {})
                .filter((k) => k !== 'lines' && k !== 'id')
                .map((k) => errorsHMRC?.errors[k])
                .map((e, i) => {
                  return (
                    <Box key={i} sx={{ p: 1, bgcolor: 'black', color: 'white', display: 'flex', flexDirection: 'row' }}>
                      <SubdirectoryArrowRightIcon />
                      <div style={{ paddingTop: 5 }}>{e.modalError ? e.modalError.msg : e.msg}</div>
                    </Box>
                  );
                })}
            </Box>
          </Popper>
        </>
      )}
    </>
  );
}

StatusCell.propTypes = {
  value: PropTypes.object,
};

const statusColumnType = {
  ...GRID_STRING_COL_DEF,
  type: 'custom',
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  display: 'flex',
  renderCell: (params) => <StatusCell {...params} />,
};

const Consignments = ({ setConsignmentsVisited }) => {
  const [selectedConsignments, setSelectedConsignments] = useState([]);
  const [FixOrContinueModalOpen, setFixOrContinueModalOpen] = useState(false);
  const { gotoStep } = useStepper();

  const {
    data,
    setData,
    errors,
    errorsHMRC,
    hasErrors,
    addNewConsignment,
    editConsignment,
    deleteConsignment,
    isReadOnly,
  } = useContext(GBENSContext);

  useEffect(() => {
    setConsignmentsVisited(true);
  }, []);

  const mappedConsignmentData = useMemo(() => {
    return [...(data?.Consignments ?? [])].map((c) => ({
      id: c.id,
      consignmentReference: c.consignmentReference,
      consignor: c.Consignor?.companyName,
      consignee: c.Consignee?.companyName,
      lines: c.lines?.length ?? 0,
      status: [
        {
          message: 'There are errors with this consignment.',
          errors:
            errors?.Consignments?.find?.(
              (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
            ) ?? null,
        },
        {
          message: 'There are HMRC errors with this consignment.',
          errors:
            errorsHMRC?.Consignments?.find?.(
              (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
            ) ?? null,
        },
      ],
      statusDescription: c.statusDescription,
    }));
  }, [data]);

  const handleSelectionChange = (selectionModel) => {
    setSelectedConsignments(selectionModel);
  };

  const handleDelete = async () => {
    if (selectedConsignments.length === 0) {
      alert('Please select consignments to delete.');
      return;
    }

    try {
      await deleteConsignment(selectedConsignments);
      // Convert the selected IDs to numbers to match consignment.id
      const selectedIds = selectedConsignments.map((id) => Number(id));

      // Update the context state by filtering out deleted consignments.
      setData((prevData) => ({
        ...prevData,
        Consignments: prevData.Consignments.filter((consignment) => !selectedIds.includes(consignment.id)),
      }));
      setSelectedConsignments([]); // Clear selection after deletion
    } catch (error) {
      console.error('Error deleting consignments:', error);
      alert('An error occurred while deleting consignments.');
    }
  };

  const updateConsignment = (id) => {
    editConsignment(id);
    gotoStep('UpdatingConsignment');
  };

  const newConsignment = () => {
    addNewConsignment();
    gotoStep('AddingConsignment');
  };

  const onNext = () => {
    if (hasErrors('Consignments') && !isReadOnly) {
      setFixOrContinueModalOpen(true);
    } else onContinue();
  };

  const onContinue = () => {
    gotoStep('AdditionalDetails');
    setFixOrContinueModalOpen(false);
  };

  const columns = [
    { field: 'status', headerName: 'Status', flex: 1, ...statusColumnType, headerClassName: 'theme--header' },
    { field: 'consignmentReference', headerName: 'Reference', flex: 1, headerClassName: 'theme--header' },
    { field: 'consignor', headerName: 'Consignor', flex: 1, headerClassName: 'theme--header' },
    { field: 'consignee', headerName: 'Consignee', flex: 1, headerClassName: 'theme--header' },
    { field: 'lines', headerName: '# of lines', flex: 1, headerClassName: 'theme--header' },
    { field: 'statusDescription', headerName: 'Status', flex: 1, headerClassName: 'theme--header' },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 1,
      headerClassName: 'theme--header',
      type: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem key={id} icon={<EditIcon />} label="Edit" onClick={() => updateConsignment(id)} />,
        ];
      },
    },
  ];
  return (
    <>
      <FixOrContinueModal
        open={FixOrContinueModalOpen}
        onFix={() => setFixOrContinueModalOpen(false)}
        onSave={onContinue}
      />
      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <div style={{ width: '100%', marginBottom: 30 }}>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 3 }}>
            Consignments added to this ENS declaration are displayed below.
          </Typography>
          <Paper sx={{ width: '100%', marginBottom: 2 }}>
            <DataGrid
              rows={mappedConsignmentData}
              columns={columns}
              pageSizeOptions={[20, 50, 100]}
              checkboxSelection
              isRowSelectable={() => !isReadOnly}
              onRowSelectionModelChange={handleSelectionChange}
              sx={{ border: 0 }}
            />
          </Paper>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {!isReadOnly ? <RightLink name="Add Consignment" onClick={newConsignment} /> : <span></span>}
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={selectedConsignments.length === 0}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={onNext}>
          Next: Additional Details
        </Button>
      </div>
    </>
  );
};

Consignments.propTypes = {
  setConsignmentsVisited: PropTypes.func.isRequired,
};

export default Consignments;
